/**
 * Dates are handled as plain `YYYY-MM-DD` calendar strings. Money is booked on
 * the day it happened where the user lives, so nothing here ever touches UTC.
 *
 * The maths here is pure. The *labels* are not — a month's name and the word
 * "yesterday" both depend on the language — so this file keeps one deliberate
 * piece of ambient state, set once at boot and again whenever the language
 * changes (`stores/i18n.js`). The alternative was threading a locale through
 * sixty call sites that do not otherwise care.
 *
 * *Today* is the second, for the same reason. `setDateClock()` hands this file
 * a function that says what day it is; the stores give it one that reads a
 * reactive `state.today` (`stores/clock.js`), so every computed and template
 * that asks for `today()` is redrawn when the date turns over — without any of
 * them knowing. With no clock set, it is the real date, which is what a test
 * or plain Node wants.
 */
const pad = (n) => String(n).padStart(2, '0')

let locale // undefined until told: the browser's own until then
let translate = null

/** @param {{locale: string, t: (key: string, params?: object) => string}} o */
export function setDateLanguage({ locale: code, t } = {}) {
  locale = code
  translate = typeof t === 'function' ? t : null
}

/** The catalogue's word, or the English one baked in here if there is none. */
const word = (key, fallback, params) => (translate ? translate(key, params) : fallback)

export const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

export function fromISO(iso) {
  const [y, m, d] = String(iso).split('-').map(Number)
  return new Date(y, m - 1, d)
}

let clock = null

/** @param {(() => string) | null} read  what day it is, as `YYYY-MM-DD` */
export function setDateClock(read) {
  clock = typeof read === 'function' ? read : null
}

/** The date on the wall, whatever the clock above says. */
export const realToday = () => toISO(new Date())

export const today = () => (clock ? clock() : realToday())

export const monthOf = (iso) => String(iso).slice(0, 7)

export const thisMonth = () => monthOf(today())

export function addDays(iso, days) {
  const d = fromISO(iso)
  d.setDate(d.getDate() + days)
  return toISO(d)
}

export function daysInMonth(year, month1) {
  return new Date(year, month1, 0).getDate()
}

/** Adds months, clamping the day to the target month (Jan 31 + 1m = Feb 28). */
export function addMonths(iso, months) {
  const d = fromISO(iso)
  const day = d.getDate()
  const target = new Date(d.getFullYear(), d.getMonth() + months, 1)
  const max = daysInMonth(target.getFullYear(), target.getMonth() + 1)
  target.setDate(Math.min(day, max))
  return toISO(target)
}

export const startOfMonth = (month) => `${month}-01`

export function endOfMonth(month) {
  const [y, m] = month.split('-').map(Number)
  return `${month}-${pad(daysInMonth(y, m))}`
}

export const shiftMonth = (month, delta) => monthOf(addMonths(`${month}-01`, delta))

/** Whole days from `a` to `b`; negative when `b` is earlier. */
export function diffDays(a, b) {
  return Math.round((fromISO(b) - fromISO(a)) / 86400000)
}

/** Fractional months between two dates, for pacing. */
export const diffMonths = (a, b) => diffDays(a, b) / 30.4375

export function monthLabel(month, { short = false } = {}) {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString(locale, {
    month: short ? 'short' : 'long',
    year: 'numeric',
  })
}

/** A date said in full: "19 Sep 2026". */
export const fullDay = (iso) =>
  fromISO(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })

/** Just the month's name, for a chart axis: "Apr". */
export const monthName = (month, { short = true } = {}) =>
  fromISO(`${month}-01`).toLocaleDateString(locale, { month: short ? 'short' : 'long' })

export function dayLabel(iso) {
  const t = today()
  if (iso === t) return word('date.today', 'Today')
  if (iso === addDays(t, -1)) return word('date.yesterday', 'Yesterday')
  if (iso === addDays(t, 1)) return word('date.tomorrow', 'Tomorrow')
  const d = fromISO(iso)
  const sameYear = d.getFullYear() === fromISO(today()).getFullYear()
  return d.toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

export function shortDay(iso) {
  return fromISO(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' })
}

/** "in 3 days" / "6 days ago" / "today" */
export function relativeDay(iso, from = today()) {
  const n = diffDays(from, iso)
  if (n === 0) return word('date.relative.today', 'today')
  if (n === 1) return word('date.relative.tomorrow', 'tomorrow')
  if (n === -1) return word('date.relative.yesterday', 'yesterday')
  return n > 0
    ? word('date.relative.in', `in ${n} days`, { count: n })
    : word('date.relative.ago', `${-n} days ago`, { count: -n })
}
